import { Button } from "@/design-system/components/ui";
import { PASTE_FIELD_LABELS, type PasteConflict } from "./mergePaste";
import type { PasteFormState } from "./pasteFormState";

/**
 * What a paste left for the specialist to decide, shown under PASTE TO FILL in
 * the New listing agent form: each field where the paste disagrees with what
 * they typed (D5 — both shown, they pick), the one line offered for Notes, and
 * how many values were left out.
 */
export function PasteReview({
  paste,
  disabled,
  onUsePasted,
  onKeepTyped,
  onAddNotes,
  onDismissNotes,
}: {
  paste: PasteFormState;
  disabled?: boolean;
  onUsePasted: (conflict: PasteConflict) => void;
  onKeepTyped: (conflict: PasteConflict) => void;
  onAddNotes: (line: string) => void;
  onDismissNotes: () => void;
}) {
  const notesOffer = paste.notesOffer;
  const nothing = paste.conflicts.length === 0 && !notesOffer && paste.droppedValueCount === 0;
  if (nothing) return null;

  return (
    <div className="grid gap-space-2" data-testid="agent-paste-review">
      {paste.conflicts.length > 0 && (
        <div
          className="grid gap-space-2 rounded-md border border-status-warning bg-white-warm px-space-3 py-space-3"
          role="group"
          aria-label="Paste differs from what you typed"
        >
          <p className="text-small font-semibold text-onyx m-0">The paste differs from what you typed</p>
          <ul className="m-0 p-0 list-none grid gap-space-2">
            {paste.conflicts.map((c) => (
              <li
                key={c.field}
                className="flex flex-wrap items-center justify-between gap-space-2"
                data-testid={`agent-paste-conflict-${c.field}`}
              >
                <span className="text-small text-onyx min-w-0 break-all">
                  {PASTE_FIELD_LABELS[c.field]}: you typed “{c.typed}” · the paste says “{c.pasted}”
                </span>
                <span className="flex gap-space-1">
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    disabled={disabled}
                    onClick={() => onUsePasted(c)}
                    aria-label={`Use pasted ${PASTE_FIELD_LABELS[c.field].toLowerCase()}`}
                    data-testid={`agent-paste-use-${c.field}`}
                  >
                    Use pasted
                  </Button>
                  <Button
                    type="button"
                    variant="text"
                    size="sm"
                    disabled={disabled}
                    onClick={() => onKeepTyped(c)}
                    aria-label={`Keep typed ${PASTE_FIELD_LABELS[c.field].toLowerCase()}`}
                    data-testid={`agent-paste-keep-${c.field}`}
                  >
                    Keep typed
                  </Button>
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {notesOffer && (
        <div
          className="flex flex-wrap items-center justify-between gap-space-2 rounded-md border border-border-light bg-white-pure px-space-3 py-space-2"
          data-testid="agent-paste-notes-offer"
        >
          <span className="text-small text-onyx min-w-0 break-all">
            <span className="text-gray-mid">Also in the paste:</span> {notesOffer}
          </span>
          <span className="flex gap-space-1">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled}
              onClick={() => onAddNotes(notesOffer)}
              data-testid="agent-paste-add-notes"
            >
              Add to notes
            </Button>
            <Button type="button" variant="text" size="sm" disabled={disabled} onClick={onDismissNotes}>
              Dismiss
            </Button>
          </span>
        </div>
      )}

      {paste.droppedValueCount > 0 && (
        <p className="text-caption text-gray-mid m-0" data-testid="agent-paste-dropped">
          {paste.droppedValueCount === 1
            ? "1 value was left out because it wasn't in the paste or wasn't valid."
            : `${paste.droppedValueCount} values were left out because they weren't in the paste or weren't valid.`}
        </p>
      )}
    </div>
  );
}
